import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import GoogleMapReact from 'google-map-react';
import FeaturedAdsCarousel from '../components/FeaturedAdsCarousel';
import PopupAd from '../components/PopupAd';
import './MapPage.css';

const BusinessMarker = ({ business, onClick, isSelected }) => (
  <div className={`map-marker ${isSelected ? 'selected' : ''}`} onClick={() => onClick(business)}>
    <div className="marker-icon">📍</div>
    <div className="marker-tooltip">{business.name || business.businessName}</div>
  </div>
);

function MapPage() {
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: -17.7333, lng: 168.3167 });
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...businesses];
    
    // Search filter - search in name, address, description, and tags
    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(b => {
        const name = (b.name || b.businessName || b.title || '').toLowerCase();
        const address = (b.address || '').toLowerCase();
        const description = (b.description || '').toLowerCase();
        const tags = Array.isArray(b.tags) ? b.tags.join(' ').toLowerCase() : (b.tags || '').toString().toLowerCase();
        
        return name.includes(query) || 
               address.includes(query) || 
               description.includes(query) ||
               tags.includes(query);
      });
    }
    
    // Category filter - handle multiple field name variations
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(b => {
        const catId = (b.categoryId || b.category || '').toString();
        return selectedCategories.includes(catId);
      });
    }
    
    // Subcategory filter - handle multiple field name variations and arrays
    if (selectedSubcategories.length > 0) {
      filtered = filtered.filter(b => {
        // Collect all possible subcategory IDs from the business
        const bizSubcatIds = new Set();
        
        // Check single subcategory ID fields
        const subcatId = (b.subcategoryId || b.subCategoryId || '').toString();
        if (subcatId) bizSubcatIds.add(subcatId);
        
        // Check array subcategory ID fields
        const subcatIds = b.subcategoryIds || b.subCategoryIds;
        if (Array.isArray(subcatIds)) {
          subcatIds.forEach(id => {
            const idStr = (id || '').toString();
            if (idStr) bizSubcatIds.add(idStr);
          });
        }
        
        // Fallback: match by subcategory name
        const subcatName = (b.subcategory || b.subCategory || '').toString().trim().toLowerCase();
        if (subcatName) {
          const matchingSubcat = subcategories.find(s => 
            s.name.trim().toLowerCase() === subcatName
          );
          if (matchingSubcat) bizSubcatIds.add(matchingSubcat.id);
        }
        
        // Check if any of the business's subcategories match the selected ones
        return selectedSubcategories.some(selectedId => bizSubcatIds.has(selectedId));
      });
    }
    
    setFilteredBusinesses(filtered);
  }, [businesses, searchQuery, selectedCategories, selectedSubcategories, subcategories]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch categories
      const categoriesSnapshot = await getDocs(collection(db, 'categories'));
      const categoriesData = categoriesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(c => c.isActive !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(categoriesData);
      
      // Fetch subcategories
      const subcategoriesSnapshot = await getDocs(collection(db, 'subcategories'));
      const subcategoriesData = subcategoriesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(s => s.isActive !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setSubcategories(subcategoriesData);
      
      // Fetch businesses
      const businessesSnapshot = await getDocs(collection(db, 'business'));
      const businessesData = businessesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(b => {
          const lat = parseFloat(b.lat || b.latitude);
          const lng = parseFloat(b.long || b.lng || b.longitude);
          return !isNaN(lat) && !isNaN(lng);
        });
      setBusinesses(businessesData);
      setFilteredBusinesses(businessesData);
      console.log('Loaded:', categoriesData.length, 'categories,', subcategoriesData.length, 'subcategories,', businessesData.length, 'businesses');
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleSubcategory = (subcategoryId) => {
    setSelectedSubcategories(prev =>
      prev.includes(subcategoryId)
        ? prev.filter(id => id !== subcategoryId)
        : [...prev, subcategoryId]
    );
  };

  const selectAllCategories = () => {
    setSelectedCategories(categories.map(c => c.id));
  };

  const clearAllCategories = () => {
    setSelectedCategories([]);
    setSelectedSubcategories([]);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategories([]);
    setSelectedSubcategories([]);
  };

  const handleMarkerClick = (business) => {
    setSelectedBusiness(business);
    const lat = parseFloat(business.lat || business.latitude);
    const lng = parseFloat(business.long || business.lng || business.longitude);
    setMapCenter({ lat, lng });
  };
  
  const openNavigation = (business) => {
    const lat = parseFloat(business.lat || business.latitude);
    const lng = parseFloat(business.long || business.lng || business.longitude);
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    window.open(url, '_blank');
  };
  
  // Get subcategories for selected categories
  const availableSubcategories = selectedCategories.length > 0
    ? subcategories.filter(s => selectedCategories.includes(s.categoryId))
    : [];

  return (
    <div className="map-page">
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading map...</p>
        </div>
      ) : (
        <>
          <FeaturedAdsCarousel 
            categoryId={selectedCategories.length === 1 ? selectedCategories[0] : null}
            subcategoryId={selectedSubcategories.length === 1 ? selectedSubcategories[0] : null}
          />
          <PopupAd 
            categoryId={selectedCategories.length === 1 ? selectedCategories[0] : null}
            subcategoryId={selectedSubcategories.length === 1 ? selectedSubcategories[0] : null}
          />
          
          {/* Map Container */}
          <div className="map-container">
            {/* Top Search Bar - Inside map container */}
            <div className="map-top-controls">
              <div className="map-search-container">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  className="map-search-input"
                  placeholder="Search businesses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button 
                    className="clear-search-map" 
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
                <button className="map-filter-btn" onClick={() => setShowFilters(!showFilters)}>
                  <span>⚙️</span>
                  {(selectedCategories.length + selectedSubcategories.length > 0) && (
                    <span className="filter-count-badge">
                      {selectedCategories.length + selectedSubcategories.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Subcategory Chips */}
              {availableSubcategories.length > 0 && (
                <div className="map-subcategory-chips">
                  {availableSubcategories.map(subcat => (
                    <button
                      key={subcat.id}
                      className={`subcat-chip ${selectedSubcategories.includes(subcat.id) ? 'active' : ''}`}
                      onClick={() => toggleSubcategory(subcat.id)}
                    >
                      {subcat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Results Counter Badge */}
            <div className="map-results-badge">
              {filteredBusinesses.length} of {businesses.length} businesses
            </div>

            <GoogleMapReact
              bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY }}
              center={mapCenter}
              defaultZoom={11}
              options={{
                styles: [
                  { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#1e2435' }] },
                  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f1419' }] },
                  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a3142' }] },
                  { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] }
                ],
                fullscreenControl: false,
                zoomControl: true,
                mapTypeControl: false,
                streetViewControl: false
              }}
            >
              {filteredBusinesses.map(business => (
                <BusinessMarker
                  key={business.id}
                  lat={parseFloat(business.lat || business.latitude)}
                  lng={parseFloat(business.long || business.lng || business.longitude)}
                  business={business}
                  onClick={handleMarkerClick}
                  isSelected={selectedBusiness?.id === business.id}
                />
              ))}
            </GoogleMapReact>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="map-filter-panel">
              <div className="filter-header">
                <h3>Filters</h3>
                <button className="close-btn" onClick={() => setShowFilters(false)}>×</button>
              </div>

              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Categories</h4>
                  <div className="filter-actions">
                    <button onClick={selectAllCategories}>Select All</button>
                    <button onClick={clearAllCategories}>Clear</button>
                  </div>
                </div>
                <p className="filter-count">{selectedCategories.length} selected</p>
                
                <div className="filter-options">
                  {categories.map(category => (
                    <label key={category.id} className="filter-option">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => toggleCategory(category.id)}
                      />
                      <span>{category.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="filter-footer">
                <button className="btn-secondary" onClick={resetFilters}>Reset</button>
                <button className="btn-primary" onClick={() => setShowFilters(false)}>Apply Filters</button>
              </div>
            </div>
          )}

          {/* Selected Business Card */}
          {selectedBusiness && (
            <div className="business-info-card">
              <button className="card-close-btn" onClick={() => setSelectedBusiness(null)}>×</button>
              
              {(selectedBusiness.shopBanner || selectedBusiness.images?.[0]) && (
                <img 
                  src={selectedBusiness.shopBanner || selectedBusiness.images[0]} 
                  alt={selectedBusiness.name || selectedBusiness.businessName}
                  className="card-image"
                />
              )}
              
              <div className="card-content">
                <h3>{selectedBusiness.name || selectedBusiness.businessName}</h3>
                
                {selectedBusiness.address && (
                  <p className="card-address">
                    <span>📍</span> {selectedBusiness.address}
                  </p>
                )}
                
                {selectedBusiness.rating && parseFloat(selectedBusiness.rating) > 0 && (
                  <p className="card-rating">
                    <span>⭐</span> {selectedBusiness.rating}
                  </p>
                )}
                
                {selectedBusiness.phone && (
                  <p className="card-phone">
                    <span>📞</span> {selectedBusiness.phone}
                  </p>
                )}
                
                <div className="card-actions">
                  <Link to={`/business/${selectedBusiness.id}`} className="btn-view-details">
                    View Details
                  </Link>
                  <button onClick={() => openNavigation(selectedBusiness)} className="btn-navigate">
                    <span>🧭</span> Navigate
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MapPage;
